import { AxiosHeaders, isAxiosError, type InternalAxiosRequestConfig } from "axios";

/** Header carrying the correlation ID of an API request; the API echoes it (CORS-exposed) in the response. */
export const CORRELATION_ID_HEADER = "X-Correlation-ID";
/** Sentry tag (errors) and span attribute name, shared with API and Lambdas. */
export const CORRELATION_ID_TAG = "correlation_id";

/** The correlation ID already set on a request config, e.g. when the 401 interceptor retries the same config. */
export function getRequestCorrelationId(requestConfig: Pick<InternalAxiosRequestConfig, "headers"> | undefined) {
  return readHeader(requestConfig?.headers, CORRELATION_ID_HEADER);
}

/**
 * The correlation ID of a failed API request: the ID echoed by the API, which is authoritative because the API replaces
 * invalid IDs, else the ID that was sent.
 */
export function getErrorCorrelationId(error: unknown): string | undefined {
  if (!isAxiosError(error)) {
    return undefined;
  }

  return readHeader(error.response?.headers, CORRELATION_ID_HEADER) ?? getRequestCorrelationId(error.config);
}

function readHeader(headers: unknown, name: string): string | undefined {
  if (typeof headers !== "object" || headers === null) {
    return undefined;
  }

  const value =
    headers instanceof AxiosHeaders ? headers.get(name) : (headers as Record<string, unknown>)[name.toLowerCase()];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}
